import { Platform } from 'react-native';
import {
  initialize,
  warmup,
  initiatePayment,
  abortTransaction as sdkAbortTransaction,
  setDisplaySettings as sdkSetDisplaySettings,
  PaymentResult,
  DisplaySettings,
} from '@oolio-group/pay-embedded';
import Sentry from './Sentry/Sentry';

interface InitConfig {
  store: string;
  merchant: string;
  accessToken: string;
  userId: string;
  terminalId: string;
}

class Tap2PayEmbeddedManager {
  private initialized = false;
  private initializing = false;
  private paymentInProgress = false;
  private lastConfig: InitConfig | null = null;

  async initialize(config: InitConfig): Promise<void> {
    if (Platform.OS !== 'android') return;
    if (this.initializing) return;
    if (
      this.initialized &&
      this.lastConfig &&
      this.lastConfig.store === config.store &&
      this.lastConfig.merchant === config.merchant &&
      this.lastConfig.accessToken === config.accessToken &&
      this.lastConfig.userId === config.userId
    ) {
      return;
    }

    this.initializing = true;
    try {
      await initialize({
        provider: 'ADYEN',
        store: config.store,
        merchant: config.merchant,
        accessToken: config.accessToken,
        userId: config.userId,
        terminalId: config.terminalId,
      });
      this.initialized = true;
      this.lastConfig = config;
    } catch (error) {
      this.initialized = false;
      this.lastConfig = null;
      throw error;
    } finally {
      this.initializing = false;
    }
  }

  async warmup(): Promise<void> {
    if (Platform.OS !== 'android') return;
    if (!this.initialized) return;
    try {
      await warmup();
    } catch (error: unknown) {
      // warmup is best-effort
      Sentry.addBreadcrumb({
        category: 'tap2pay-embedded',
        message: 'warmup() failed (best-effort, ignored)',
        level: 'warning',
        data: { error: String(error) },
      });
    }
  }

  async setDisplaySettings(
    settings: DisplaySettings,
  ): Promise<{ success: boolean }> {
    if (Platform.OS !== 'android') return { success: false };
    return sdkSetDisplaySettings(settings);
  }

  async abortTransaction(): Promise<void> {
    if (Platform.OS !== 'android') return;
    if (!this.paymentInProgress) return;
    try {
      await sdkAbortTransaction();
    } catch (error: unknown) {
      // best-effort
      Sentry.addBreadcrumb({
        category: 'tap2pay-embedded',
        message: 'abortTransaction() failed (best-effort, ignored)',
        level: 'warning',
        data: { error: String(error) },
      });
    } finally {
      this.paymentInProgress = false;
    }
  }

  get isInitialized(): boolean {
    return this.initialized;
  }

  get isPaymentInProgress(): boolean {
    return this.paymentInProgress;
  }

  async pay(params: {
    amount: string;
    currency: string;
    merchantReference?: string;
    tipAmount?: string;
  }): Promise<PaymentResult> {
    if (Platform.OS !== 'android') {
      Sentry.addBreadcrumb({
        category: 'tap2pay-embedded',
        message: 'pay() rejected: not Android',
        level: 'warning',
      });
      return {
        success: false,
        status: 'ERROR',
        errorMessage: 'Tap2Pay Embedded is only available on Android',
      };
    }

    if (!this.initialized) {
      Sentry.addBreadcrumb({
        category: 'tap2pay-embedded',
        message: 'pay() rejected: SDK not initialized',
        level: 'warning',
      });
      return {
        success: false,
        status: 'ERROR',
        errorMessage: 'SDK not initialized. Call initialize() first.',
      };
    }

    if (this.paymentInProgress) {
      Sentry.addBreadcrumb({
        category: 'tap2pay-embedded',
        message: 'pay() rejected: payment already in progress',
        level: 'warning',
      });
      return {
        success: false,
        status: 'ERROR',
        errorMessage: 'A payment is already in progress.',
      };
    }

    this.paymentInProgress = true;
    try {
      const result = await initiatePayment({
        amount: params.amount,
        currency: params.currency,
        merchantReference: params.merchantReference,
        tipAmount: params.tipAmount,
      });
      return result;
    } finally {
      this.paymentInProgress = false;
    }
  }
}

export const tap2PayEmbeddedManager = new Tap2PayEmbeddedManager();
